// Landing-page vault card renderer. Single source of truth:
//   - Metadata (name, subtitle, icon, colors, projected metrics) ← js/config.js
//   - Live TVL + share price ← on-chain via ethers + mainnet RPC
//
// Any fleet addition / metric update happens in config.js and automatically
// reflects on both crystalclear.finance (landing) and app.crystalclear.finance
// (app). No hand-maintained TVL/share-price values in this file.

// Register the same-origin service worker (see /sw.js). Cache-first for
// static assets means subsequent visits paint in <100ms.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((e) => {
      console.warn('SW registration failed:', e);
    });
  });
}

import { GEMSTONES, NETWORK, VAULT_CONFIGS, APP_MODE } from './config.js';
import { applyStealthToLandingHero } from './stealth-mode.js';
import { wireHowItWorksTriggers } from './how-it-works.js';
import { refreshAllAprCells, refreshAllMaxDdCells, refreshAllSharePriceCells } from './performance.js';

// Minimal ABI — just the reads we need for landing cards.
const VAULT_ABI = [
  'function totalAssets() view returns (uint256)',
  'function totalSupply() view returns (uint256)',
  'function convertToAssets(uint256) view returns (uint256)',
];
const USDC_DECIMALS = 6;
const SHARE_DECIMALS = 9;

function metric(label, value, valueClass = 'text-on-surface', subtitle = null) {
  const sub = subtitle
    ? `<span class="text-[0.55rem] uppercase tracking-wider text-on-surface-variant/60 block mt-0.5">${subtitle}</span>`
    : '';
  return `<div>
    <span class="text-xs uppercase tracking-wider text-on-surface-variant block mb-1">${label}</span>
    <div class="text-xl font-headline font-bold ${valueClass}" data-metric="${label.toLowerCase().replace(' ', '-')}">${value}</div>
    ${sub}
  </div>`;
}

function pill(text, live) {
  const cls = live
    ? 'bg-surface-container-low text-secondary-fixed'
    : 'bg-surface-container text-on-surface-variant';
  return `<span class="${cls} text-xs px-2.5 py-1 rounded-DEFAULT border border-outline-variant/15">${text}</span>`;
}

// Capacity bar — reframes a small seed as "early / room to grow", not "empty".
// We seed every vault with our own capital; the bar shows that seed against an
// early-stage capacity target. Fill is floored to a visible sliver and capped
// below full so there's always headroom; the honest $ TVL lives in the caption
// (filled live by updateCard). Cyan-teal brand gradient on a muted track.
function capacityTile(reveal, isClosed) {
  const initFill = reveal ? '6%' : '0%';
  const initCap  = reveal ? '…' : '—';
  // Closed seed vaults: framed as "Seed" (not "Capacity / room to grow" — they
  // don't take new deposits). Open vaults keep the capacity framing.
  const label = isClosed ? 'Seed' : 'Capacity';
  const title = isClosed
    ? 'Proprietary seed capital — this vault is closed to new deposits.'
    : 'Proprietary seed capital — early stage, room to grow.';
  return `<div>
    <span class="text-xs uppercase tracking-wider text-on-surface-variant block mb-1">${label}</span>
    <div class="mt-2.5" data-metric="capacity" title="${title}">
      <div class="h-1.5 rounded-full bg-on-surface/10 overflow-hidden">
        <div data-capacity-fill class="h-full rounded-full transition-all duration-700"
             style="width:${initFill};background:linear-gradient(90deg,#6af2de,#2fd9f4)"></div>
      </div>
      <span data-capacity-caption class="text-[0.55rem] uppercase tracking-wider text-on-surface-variant/60 block mt-1.5">${initCap}</span>
    </div>
  </div>`;
}

function card(vault) {
  // Resolve gemstone metadata. Normally keyed by vault.key, but a RETIRED
  // instance carries an instance-suffixed key (e.g. "diamond_v1") that has no
  // GEMSTONES template entry — fall back to the config entry itself, which
  // already has the gemstone props spread into it via `...GEMSTONES.<name>`.
  // Without this fallback, GEMSTONES[key] is undefined and `g.metrics` throws
  // *inside the .map()*, blanking the ENTIRE vault grid. (Regression 2026-05-23.)
  const g = GEMSTONES[vault.key] || vault;
  const live = vault.status === 'live';
  // In stealth mode, even "live" vaults get the muted/coming-soon treatment
  // for all user-facing performance metrics. The vault is still technically
  // live + trading; we just don't advertise numbers yet.
  const reveal = live && !APP_MODE.stealth;
  const m = g.metrics || {};

  // APR tile is the headline hype number. Always colored (even in stealth)
  // because we DO advertise it — it's a conservative backtest estimate, not a
  // live performance claim. The rest stay muted until launch reveals them.
  const aprClass    = 'text-primary';
  const winClass    = reveal ? 'text-on-surface' : 'text-on-surface-variant';
  const ddClass     = reveal ? 'text-on-surface' : 'text-on-surface-variant';
  const rest        = reveal ? 'text-on-surface' : 'text-on-surface-variant';

  const statusBadge = reveal
    ? `<span class="bg-surface-container text-primary text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-sm border border-primary/20 flex items-center gap-1">
         <span class="w-1.5 h-1.5 rounded-full bg-primary animate-ping"></span> Live
       </span>`
    : live
      ? `<span class="bg-surface-container text-primary/70 text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-sm border border-primary/20 flex items-center gap-1">
           <span class="w-1.5 h-1.5 rounded-full bg-primary animate-ping"></span> Trading
         </span>`
      : `<span class="bg-surface-container text-on-surface-variant text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-sm border border-outline-variant/30">Coming Soon</span>`;

  // Seed-vault badge: the deposit-closed vaults (Onyx/Ruby/Emerald/Moonstone) are
  // our proprietary seed vaults. The open vault (Amber) gets no badge.
  const seedBadge = vault.deposit_closed
    ? `<span class="bg-surface-container text-on-surface-variant text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-sm border border-outline-variant/25">Seed vault</span>`
    : '';

  const cta = reveal
    ? `<a href="app.html#vault/${vault.key}" class="block w-full text-center py-3 bg-gradient-to-br from-primary to-primary-container text-on-primary-container font-headline font-bold hover:shadow-[0_0_24px_rgba(138,235,255,0.4)] transition-all duration-300 active:scale-[0.98]">View Vault</a>`
    : APP_MODE.stealth && live
      ? `<a href="app.html#vault/${vault.key}" class="block w-full text-center py-3 bg-surface-container-high text-secondary font-headline hover:bg-surface-variant/50 border border-outline-variant/30 transition-all duration-300">Preview — opens ${new Date(APP_MODE.launch_date).toLocaleDateString('en-US',{month:'short',day:'numeric'})}</a>`
      : `<div class="w-full text-center py-3 bg-surface-container-high text-on-surface-variant font-headline cursor-not-allowed border border-outline-variant/15">Launching soon</div>`;

  // In stealth mode, performance ratios (Sharpe/Win/MDD) and user-data fields
  // (Share Price/TVL) are hidden. Target APR + trades/yr + coin count DO show
  // — they're vault-structure / conservative-backtest claims we're comfortable
  // advertising pre-launch.
  const fmt = (v, unit = '') => (!reveal || v == null) ? '—' : `${v}${unit}`;
  const aprVal = m.targetApr != null ? `~${m.targetApr}%` : '—';

  // Live vaults render a loading shimmer in the APR cell that refreshAllAprCells()
  // swaps for the real Live APR — avoids flashing the backtest "Target APR"
  // number and then changing it. Coming-soon vaults keep static Target APR.
  const aprLabelTxt = live ? 'APR' : 'Target APR';
  const aprValTxt = live
    ? '<span class="inline-block w-14 h-5 rounded bg-on-surface/10 animate-pulse align-middle"></span>'
    : aprVal;
  const aprSubTxt = live ? '&nbsp;' : 'backtest est.';

  // Share price + TVL: show real values only when revealed. In stealth we
  // don't even render the on-chain read (dashed placeholder stays forever).
  const initialSharePrice = reveal ? '…' : '—';
  const initialTvl        = reveal ? '…' : '—';

  const wrapperCls = live
    ? `glass-panel p-8 ghost-border rounded-lg hover:border-[${g.color.hex}]/30 transition-all duration-300 relative overflow-hidden group`
    : 'glass-panel p-8 ghost-border rounded-lg opacity-60';

  return `<div class="${wrapperCls}" data-vault-key="${vault.key}">
    ${live ? `<div class="absolute inset-0 bg-gradient-to-br from-[${g.color.hex}]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>` : ''}
    <div class="${live ? 'relative z-10' : ''}">
      <div class="flex items-center gap-4 mb-5">
        <div class="w-12 h-12 bg-[${g.color.hex}]/10 rounded-lg flex items-center justify-center">
          <span class="material-symbols-outlined text-[${g.color.hex}] text-2xl">${g.icon}</span>
        </div>
        <div>
          <div class="flex items-center gap-3">
            <h4 class="font-headline text-xl font-bold text-white">${g.name}</h4>
            ${statusBadge}
            ${seedBadge}
          </div>
          <span class="text-[10px] uppercase tracking-[0.15em] font-bold text-secondary-fixed-dim">${g.subtitle}</span>
        </div>
      </div>
      <div class="grid grid-cols-3 gap-6 mb-5">
        <div data-apr-cell="${vault.key}">
          <span data-apr-label class="text-xs uppercase tracking-wider text-on-surface-variant block mb-1">${aprLabelTxt}</span>
          <div data-apr-value class="text-xl font-headline font-bold ${aprClass}">${aprValTxt}</div>
          <span data-apr-subtitle class="text-[0.55rem] uppercase tracking-wider text-on-surface-variant/60 block mt-0.5">${aprSubTxt}</span>
        </div>
        ${metric('Win Rate', fmt(m.winRate, '%'), winClass)}
        ${metric('Max DD', reveal ? '<span class="inline-block w-12 h-5 rounded bg-on-surface/10 animate-pulse align-middle"></span>' : fmt(m.maxDD, '%'), ddClass)}
        ${metric('Share Price', initialSharePrice, rest)}
        ${capacityTile(reveal, vault.deposit_closed)}
        ${metric('Avg Hold', fmt(m.medianHoldHours, 'h'), rest)}
      </div>
      <div class="flex flex-wrap gap-2 mb-6">
        ${pill('20% Perf', live)}
        ${pill(`${m.coins ?? '--'} coins`, live)}
        ${pill(m.timeframes || '30m–2h', live)}
      </div>
      ${cta}
    </div>
  </div>`;
}

// Read on-chain state for one live vault. Best-effort — swallows errors and
// leaves the placeholder in place. We don't want a single flaky RPC call
// pulling down the whole grid render.
async function fetchVaultState(provider, vault) {
  try {
    const contract = new ethers.Contract(vault.address, VAULT_ABI, provider);
    const [totalAssets, totalSupply] = await Promise.all([
      contract.totalAssets(),
      contract.totalSupply(),
    ]);
    const tvl = Number(totalAssets) / 10 ** USDC_DECIMALS;
    let sharePrice = 1.0;
    if (totalSupply > 0n) {
      const assetsPerShare = await contract.convertToAssets(10n ** BigInt(SHARE_DECIMALS));
      const rawPrice = Number(assetsPerShare) / 10 ** USDC_DECIMALS;
      sharePrice = rawPrice / (vault.launchSharePrice || 1.0);
    }
    return { tvl, sharePrice };
  } catch (e) {
    console.warn(`[landing-vaults] on-chain read failed for ${vault.key}:`, e);
    return null;
  }
}

// Early-stage capacity target per vault — purely for the bar's fill math (we do
// NOT display this number, so it makes no on-record capacity claim). The fill is
// floored to a visible sliver and capped below full so a seed always reads as
// "early, room to grow" rather than "empty".
const CAPACITY_TARGET_USD = 50000;

function updateCard(vaultKey, { tvl, sharePrice }) {
  const el = document.querySelector(`[data-vault-key="${vaultKey}"]`);
  if (!el) return;
  const fillEl = el.querySelector('[data-capacity-fill]');
  const capEl  = el.querySelector('[data-capacity-caption]');
  const spEl   = el.querySelector('[data-metric="share-price"]');
  if (fillEl) {
    const pct = Math.min(92, Math.max(6, (tvl / CAPACITY_TARGET_USD) * 100));
    fillEl.style.width = pct.toFixed(1) + '%';
  }
  // Honest real TVL, rounded to whole dollars. Closed seed vaults read "$X seed"
  // (no "room to grow" — they don't take new deposits); the open vault keeps it.
  if (capEl) {
    const cfg = VAULT_CONFIGS.find((v) => v.key === vaultKey);
    const dollars = '$' + Math.round(tvl);
    capEl.textContent = (cfg && cfg.deposit_closed) ? `${dollars} seed` : `${dollars} · room to grow`;
  }
  if (spEl)  spEl.textContent  = '$' + sharePrice.toFixed(4);
}

async function refreshLiveCards() {
  // Stealth mode: skip on-chain reads entirely. Metrics stay at the "—"
  // placeholder set in the initial render.
  if (APP_MODE.stealth) return;
  if (typeof ethers === 'undefined') {
    console.warn('[landing-vaults] ethers not loaded — live TVL/share price unavailable');
    return;
  }
  const provider = new ethers.JsonRpcProvider(NETWORK.rpcUrl);
  // Public-only: never read on-chain state for visibility=internal vaults
  // (would surface live TVL via the card refresh even if the card itself
  // is filtered out below).
  const liveVaults = VAULT_CONFIGS.filter(
    v => v.status === 'live' && v.address && (v.visibility ?? 'public') === 'public'
  );
  await Promise.all(liveVaults.map(async (v) => {
    const state = await fetchVaultState(provider, v);
    if (state) updateCard(v.key, state);
  }));
}

function render() {
  // Swap hero CTA first (purely DOM; doesn't depend on card grid)
  applyStealthToLandingHero();
  // Wire the "How it works" nav link (idempotent — safe to call repeatedly).
  wireHowItWorksTriggers();

  const container = document.getElementById('vault-cards-grid');
  if (!container) return;
  // Live vaults first, then coming_soon + retired (both render as "Coming
  // Soon" placeholders — see GEMSTONE_RETIREMENT_DESIGN.md). Within a bucket,
  // preserve config order. Internal-visibility vaults are filtered out
  // (defence-in-depth alongside the registry-side API filter).
  // `live` (line ~53) is false for retired → the card auto-renders the
  // "Coming Soon" badge + no metrics, identical to a never-launched gemstone.
  const statusRank = (s) => (s === 'live' ? 0 : 1);  // retired + coming_soon both rank 1
  const vaults = VAULT_CONFIGS
    .filter(v => (v.visibility ?? 'public') === 'public')
    .filter(v => v.status === 'live' || v.status === 'coming_soon' || v.status === 'retired')
    .sort((a, b) => statusRank(a.status) - statusRank(b.status));
  container.innerHTML = vaults.map(card).join('\n');
  // Kick off async on-chain refresh. Cards are already visible with
  // placeholders; state fills in when the RPC returns (skipped in stealth).
  refreshLiveCards();
  // Upgrade target-APR cells to live-APR for vaults with ≥ 14 d of data.
  // No-op in stealth (helper returns target results without fetching).
  refreshAllAprCells();
  // Upgrade Max DD tiles from the static backtest value to the LIVE drawdown.
  refreshAllMaxDdCells();
  // Upgrade Share Price from the client-side on-chain read to the reliable
  // Lambda value (4 dp to match this surface), so a rate-limited browser no
  // longer silently shows the $1.00 default. Same fix as the app cards.
  refreshAllSharePriceCells(4);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', render);
} else {
  render();
}
